/**
 * Leerzustand.
 *
 * Sagt immer, **warum** nichts da ist und was als Nächstes zu tun wäre. Eine
 * leere Fläche ohne Erklärung lässt offen, ob noch geladen wird, ob die Suche
 * nichts gefunden hat oder ob die Verbindung fehlt.
 *
 * Handoff 2b nennt drei Arten — alle drei baut man mit denselben vier Feldern
 * (`icon`/`title`/`body`/`action`), es gibt bewusst keine eigene `variant`-Prop
 * dafür: die Unterschiede liegen allein im Text und im mitgegebenen Symbol.
 *
 * 1. **Wirklich leer** — nur `title` (+ optional `body` als Grund), `action`
 *    für die Anlage, falls möglich.
 *    `<EmptyState icon={<IconCheck />} title="Nichts offen in diesem Raum" body="…" />`
 * 2. **Suche/Filter ohne Treffer, aber es gibt welche ohne Filter** — `body`
 *    nennt die Trefferzahl ohne Filter im Klartext (nie nur "keine Treffer"),
 *    `action` hebt den Filter auf.
 *    `<EmptyState icon={<IconSearch />} title="Kein Vorgang passt zu „dachrinne“"
 *      body="Filter aktiv: Objekt EL, nur offen. Ohne Objektfilter gibt es 3 Treffer."
 *      action={<Button variant="secondary" onClick={clearFilter}>Objektfilter aufheben</Button>} />`
 * 3. **Struktur fehlt** — `body` erklärt, was daran hängt, `action` ist die
 *    Anlage-CTA (primär, da der einzige sinnvolle nächste Schritt).
 *    `<EmptyState title="Für dieses Objekt ist noch keine Struktur erfasst" body="…"
 *      action={<Button variant="primary" onClick={openQuickSetup}>Struktur anlegen</Button>} />`
 */
import type { ReactNode } from "react";

import { IconEmpty } from "@/components/icons";
import { cn } from "@/lib/cn";

export interface EmptyStateProps {
  title: string;
  body?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ title, body, icon, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-3 rounded-card border border-dashed border-line-strong",
        "bg-surface-raised px-5 py-10 text-center",
        className,
      )}
    >
      <span className="text-ink-faint [&>svg]:size-8">{icon ?? <IconEmpty />}</span>
      <h3 className="text-[1.0625rem] font-semibold text-ink">{title}</h3>
      {body !== undefined && <p className="max-w-sm text-[0.9375rem] text-ink-muted">{body}</p>}
      {action}
    </div>
  );
}
